// frontend/src/components/LandingSignupModal.js
// Scroll-triggered "start a real account" prompt used ONLY on the marketing
// landing (/why-ogestyle -> DemoLandingPage). Not an exit-intent modal: it
// fires once per session when the visitor scrolls a specific section (the
// sentinel element) into view, and gives them a path to the real signup flow
// ("Join OGESTYLE") instead of only the demo.
//
// Props:
//   triggerKey     — unique id used for the once-per-session flag
//   sentinelRef    — React ref to the element whose scroll position arms the modal
//   eyebrow        — small uppercase label
//   title          — heading
//   body           — supporting copy
//   note           — optional small reassurance line (free trial / no contract)
//   icon           — optional JSX icon shown in a round badge
//   primaryLabel   — primary CTA text (defaults to "Start your free trial")
//   primaryHref    — destination for the primary CTA
//   secondaryLabel — secondary control label
//   secondaryHref  — optional: if set, secondary is an <a> (e.g. live walkthrough);
//                    otherwise it is a "dismiss" button.

import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FaArrowRight, FaCheckCircle } from "react-icons/fa";
import "./LandingSignupModal.css";

// Fire a GA4 event only when a real gtag.js tag is actually loaded on the
// host (www/demo). Never throws — analytics must not break the UX.
function trackGAEvent(name, params) {
  try {
    if (
      typeof window !== "undefined" &&
      typeof window.gtag === "function" &&
      document.querySelector('script[src*="googletagmanager"]')
    ) {
      window.gtag("event", name, params || {});
    }
  } catch (err) {
    // ignore — analytics only
  }
}

export default function LandingSignupModal({
  triggerKey,
  sentinelRef,
  eyebrow,
  title,
  body,
  note,
  icon = null,
  primaryLabel = "Start your free trial",
  primaryHref = "#",
  secondaryLabel = "Keep exploring",
  secondaryHref = null,
}) {
  const [open, setOpen] = useState(false);

  // Don't nudge people who already have an account (logged in).
  const isLoggedIn =
    typeof window !== "undefined" &&
    Boolean(window.localStorage.getItem("ogestyle_token"));

  useEffect(() => {
    const storeKey = `ogestyleLandingModal_${triggerKey}`;
    if (!sentinelRef?.current || isLoggedIn) return undefined;
    if (sessionStorage.getItem(storeKey) === "1") return undefined;

    const el = sentinelRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        sessionStorage.setItem(storeKey, "1"); // once per session, even if dismissed
        observer.disconnect();
        setOpen(true);
      },
      {
        root: null,
        // Trigger once the section has actually scrolled into the viewport.
        rootMargin: "0px 0px -8% 0px",
        threshold: 0,
      }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [sentinelRef, triggerKey, isLoggedIn]);

  // Lock body scroll while the modal is open.
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // GA4 conversion event: signup prompt was shown to the visitor.
  useEffect(() => {
    if (open) {
      trackGAEvent("signup_modal_shown", { modal: triggerKey });
    }
  }, [open, triggerKey]);

  if (!open) return null;

  const close = () => setOpen(false);

  return (
    <div className="lsmodal" role="dialog" aria-modal="true" aria-label={eyebrow}>
      <button
        type="button"
        className="lsmodal__backdrop"
        onClick={close}
        aria-label="Dismiss"
      />
      <div className="lsmodal__card">
        <button
          type="button"
          className="lsmodal__close"
          onClick={close}
          aria-label="Close"
        >
          ×
        </button>

        {icon && <span className="lsmodal__badge" aria-hidden="true">{icon}</span>}

        <p className="lsmodal__eyebrow">{eyebrow}</p>
        <h2 className="lsmodal__title">{title}</h2>
        <p className="lsmodal__body">{body}</p>

        {note && (
          <p className="lsmodal__note">
            <FaCheckCircle aria-hidden="true" /> {note}
          </p>
        )}

        <div className="lsmodal__actions">
          {typeof primaryHref === "string" && primaryHref.startsWith("/") ? (
            <Link
              className="lsmodal__btn lsmodal__btn--primary"
              to={primaryHref}
              onClick={() =>
                trackGAEvent("start_free_trial_clicked", { modal: triggerKey })
              }
            >
              {primaryLabel} <FaArrowRight aria-hidden="true" />
            </Link>
          ) : (
            <a
              className="lsmodal__btn lsmodal__btn--primary"
              href={primaryHref}
              rel="noreferrer noopener"
              onClick={() =>
                trackGAEvent("start_free_trial_clicked", { modal: triggerKey })
              }
            >
              {primaryLabel} <FaArrowRight aria-hidden="true" />
            </a>
          )}
          {secondaryHref ? (
            typeof secondaryHref === "string" && secondaryHref.startsWith("/") ? (
              <Link
                className="lsmodal__btn lsmodal__btn--ghost"
                to={secondaryHref}
              >
                {secondaryLabel}
              </Link>
            ) : (
              <a
                className="lsmodal__btn lsmodal__btn--ghost"
                href={secondaryHref}
                rel="noreferrer noopener"
              >
                {secondaryLabel}
              </a>
            )
          ) : (
            <button
              type="button"
              className="lsmodal__btn lsmodal__btn--ghost"
              onClick={close}
            >
              {secondaryLabel}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
